import { Tableau } from '../../common/utilities/TableauLog';
import { L10n } from '../../ts/L10n/L10n';
import { L10nScriptLoader } from '../utils/L10nScriptLoader';

const supportedLocales: { [legacyLanguageLocale: string]: string } = {
  de: 'de_DE',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  ga: 'ga_IE',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KR',
  pt: 'pt_BR',
  zh: 'zh_CN',

  de_DE: 'de_DE',
  en_GB: 'en_GB',
  en_US: 'en_US',
  es_ES: 'es_ES',
  fr_FR: 'fr_FR',
  ga_IE: 'ga_IE',
  it_IT: 'it_IT',
  ja_JP: 'ja_JP',
  ko_KR: 'ko_KR',
  pt_BR: 'pt_BR',
  zh_CN: 'zh_CN',
  zh_TW: 'zh_TW'
};

/**
 * The role of this class is to ensure the right message and locale js files are
 * loaded in VizportalClient.
 */
class LocalizationLoaderService {

  private defaultLanguage = 'en';
  private defaultLocale = 'en_US';

  public selectLocaleOrDefault(k: string): string {
    return supportedLocales[k] || this.defaultLocale;  // clearly communicates default
  }

  public updateLanguageAndLocaleInClient(language: string, locale: string): Promise<any> {
    // Intentially not setup as default parameters, want them to be required.
    const newLanguage = language || this.defaultLanguage;
    const newLocale = locale || this.defaultLocale;

    const languagePromise = this.maybeUpdateLanguageInClient(newLanguage).catch(() => {
      Tableau.Logger().error(`Failed to read UI strings for '${newLanguage}'. Falling back to '${this.defaultLanguage}'`);
      // TODO: ideally perhaps we'd revert the language update on the server here?
      this.maybeUpdateLanguageInClient(this.defaultLanguage);
    });
    const localePromise = this.maybeUpdateLocaleInClient(newLocale).catch(() => {
      this.maybeUpdateLocaleInClient(this.defaultLocale);
    });

    return Promise.all([languagePromise, localePromise]);
  }

  private maybeUpdateLanguageInClient(newLanguage: string): Promise<any> {
    const oldLanguage = L10n.language;

    if (newLanguage !== oldLanguage) {
      const messageLocaleId = this.selectLocaleOrDefault(newLanguage);
      if (!supportedLocales[newLanguage]) {
        Tableau.Logger().error(`Unrecognized language '${newLanguage}'. Falling back to '${this.defaultLocale}'`);
      }
      L10n.language = messageLocaleId.slice(0, 2);
      return L10nScriptLoader.getLocalizeMessages(messageLocaleId);
    }

    return Promise.resolve();
  }

  private maybeUpdateLocaleInClient(newLocale: string): Promise<any> {
    const oldLocale = L10n.locale;
    if (newLocale !== oldLocale) {
      L10n.locale = newLocale;
      return L10nScriptLoader.getLocalizeFormattersAndParsers(newLocale);
    }
    return Promise.resolve();
  }
}

export const LocalizationLoader = new LocalizationLoaderService();
